import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { email } from '@vuelidate/validators';

export const MODE = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
};

export const INPUT_TYPES = {
  EMAIL: 'email',
  TEL: 'tel',
  TEXT: 'text',
};

// `defaultCountry` (ISO 3166-1 alpha-2) lets a local number such as 0567893412
// validate and resolve to E.164 against that country, as the inbox-first compose needs.
export const validatePhoneNumber = (value, defaultCountry) => {
  if (!value) return true;
  try {
    return isValidPhoneNumber(value, defaultCountry);
  } catch (error) {
    return false;
  }
};

export const formatPhoneNumber = (value, defaultCountry) => {
  try {
    const phoneNumber = parsePhoneNumber(value, defaultCountry);
    return {
      isValid: phoneNumber?.isValid() || false,
      formattedValue: phoneNumber?.formatInternational() || value,
      e164: phoneNumber?.number || value,
    };
  } catch (error) {
    return { isValid: false, formattedValue: value, e164: value };
  }
};

export const getValidationRules = (type, defaultCountry) => ({
  newTag: {
    ...(type === INPUT_TYPES.EMAIL ? { email } : {}),
    ...(type === INPUT_TYPES.TEL
      ? {
          isValidPhone: defaultCountry
            ? value => validatePhoneNumber(value, defaultCountry)
            : validatePhoneNumber,
        }
      : {}),
  },
});

export const checkTagTypeValidity = (type, value, v$, defaultCountry) => {
  if (type === INPUT_TYPES.TEL) {
    return !validatePhoneNumber(value, defaultCountry);
  }
  return v$.$invalid;
};

export const validateAndFormatNewTag = (
  trimmedNewTag,
  type,
  isNewTagInValidType,
  defaultCountry
) => {
  let isValid = true;
  let formattedValue = trimmedNewTag;
  let value = trimmedNewTag;

  if (type === INPUT_TYPES.EMAIL) {
    isValid = !isNewTagInValidType;
  } else if (type === INPUT_TYPES.TEL) {
    const {
      isValid: phoneValid,
      formattedValue: phoneFormatted,
      e164,
    } = formatPhoneNumber(trimmedNewTag, defaultCountry);
    isValid = phoneValid;
    formattedValue = phoneFormatted;
    value = e164;
  }

  return { isValid, formattedValue, value };
};

export const createNewTagMenuItem = (formattedValue, trimmedNewTag, type) => ({
  label: formattedValue,
  value: trimmedNewTag,
  ...(type === INPUT_TYPES.EMAIL ? { email: trimmedNewTag } : {}),
  ...(type === INPUT_TYPES.TEL ? { phoneNumber: trimmedNewTag } : {}),
  thumbnail: { name: formattedValue, src: '' },
  action: 'create',
});

export const buildTagMenuItems = ({
  mode,
  tags,
  menuItems,
  newTag,
  isLoading,
  type,
  isNewTagInValidType,
  allowCreate = true,
  skipLabelDedup = false,
  defaultCountry,
}) => {
  if (mode === MODE.SINGLE && tags.length >= 1) return [];

  const availableMenuItems = skipLabelDedup
    ? menuItems
    : menuItems.filter(item => !tags.includes(item.label));

  // Show typed value as suggestion only if:
  // 1. There's a value being typed
  // 2. The value isn't already in the tags
  // 3. Validation passes (email/phone) and There are no menu items available
  // 4. allowCreate is enabled
  const trimmedNewTag = newTag?.trim();
  const shouldShowCreateSuggestion =
    allowCreate &&
    trimmedNewTag &&
    !tags.includes(trimmedNewTag) &&
    !isLoading &&
    !availableMenuItems.length;

  if (shouldShowCreateSuggestion) {
    const { isValid, formattedValue, value } = validateAndFormatNewTag(
      trimmedNewTag,
      type,
      isNewTagInValidType,
      defaultCountry
    );

    if (isValid) {
      return [createNewTagMenuItem(formattedValue, value, type)];
    }
  }

  return availableMenuItems;
};

export const canAddTag = (mode, tagsLength) =>
  !(mode === MODE.SINGLE && tagsLength >= 1);

export const findMatchingMenuItem = (menuItems, value) =>
  menuItems.find(item => item.email === value);
